import { useEffect, useMemo, useState } from 'react';
import { Layer, Source } from 'react-map-gl';
import { FormattedMessage } from 'react-intl';
import WaypointPopup from '../WaypointPopup';
import useMap from '../../hooks/useMap';
import useSvgImages from '../../utils/useSvgImages';
import { defaultSymbolLayerLayoutProps, endMarkerId, getLayerBeforeId, startEndMarkerId, startMarkerId } from '../../utils/layers';
import startSvg, { svgSizeWithoutShadow as startSvgSizeWithoutShadow } from '../../utils/svgDefinitions/startSvg';
import endSvg, { svgSizeWithoutShadow as endSvgSizeWithoutShadow } from '../../utils/svgDefinitions/endSvg';
import startEndSvg, { svgSizeWithoutShadow as startEndSvgSizeWithoutShadow } from '../../utils/svgDefinitions/startEndSvg';

type StartEndMarkerProps = {
  latitude: number;
  longitude: number;
  variant: 'start' | 'end' | 'start-end';
};

const StartEndMarker = ({ latitude, longitude, variant }: StartEndMarkerProps) => {
  const map = useMap();

  const { layerId, svgDefinitions, svgSizeWithoutShadow, title } = useMemo(() => {
    switch (variant) {
      case 'start':
        return {
          layerId: startMarkerId,
          svgDefinitions: [startSvg],
          svgSizeWithoutShadow: startSvgSizeWithoutShadow,
          title: <FormattedMessage defaultMessage="Start" />
        };
      case 'end':
        return {
          layerId: endMarkerId,
          svgDefinitions: [endSvg],
          svgSizeWithoutShadow: endSvgSizeWithoutShadow,
          title: <FormattedMessage defaultMessage="End" />
        };
      case 'start-end':
        return {
          layerId: startEndMarkerId,
          svgDefinitions: [startEndSvg],
          svgSizeWithoutShadow: startEndSvgSizeWithoutShadow,
          title: <FormattedMessage defaultMessage="Start / End" />
        };
    }
  }, [variant]);

  const sourceId = useMemo(() => `${layerId}-source`, [layerId]);

  const { imagesAreLoaded } = useSvgImages(svgDefinitions);
  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    map?.on('mouseenter', layerId, () => {
      setShowPopup(true);
    });

    map?.on('mouseleave', layerId, () => {
      setShowPopup(false);
    });
  }, [map, layerId]);

  if (!imagesAreLoaded) {
    return null;
  }

  return (
    <>
      <Source
        id={sourceId}
        type="geojson"
        data={{
          type: 'Point',
          coordinates: [longitude, latitude]
        }}
      >
        <Layer
          id={layerId}
          beforeId={getLayerBeforeId(map, layerId)}
          source={sourceId}
          type="symbol"
          layout={{
            ...defaultSymbolLayerLayoutProps,
            'icon-image': svgDefinitions[0].name
          }}
        />
      </Source>
      {showPopup && <WaypointPopup anchorSize={svgSizeWithoutShadow} latitude={latitude} longitude={longitude} title={title} />}
    </>
  );
};

export default StartEndMarker;
